import React, { useEffect, useState } from 'react';
import { usePrevious, useUnmount } from 'react-use';
import { connect } from 'react-redux';
import { Container, ListGroup, ListGroupItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import { isObject, isArray } from 'lodash';
import moment from 'moment-timezone';

import {
  useTealium,
  useAdGlare,
  useNotFound,
  useDebounceLoading,
  useDelayJsLoadiing,
  useURLChangeCallback,
} from '../../utils/hookHelper';
import {
  forceToArray,
  constructPath,
  sanitizedHTML,
  momentUTCDate,
  isSSOLoggedIn,
} from '../../utils/Helper';

import PaginationWidget from '../../widgets/PaginationWidget';
import store from '../../redux/store';
import { getIndexPages, resetIndexPages } from '../../redux/modules/indexPage/action';
import { getIndexPagesList, resetIndexPagesList } from '../../redux/modules/indexPageList/action';
import HelmetMeta from '../../components/HelmetMeta';
import HelmetTitle from '../../components/HelmetTitle';
import HelmetStyle from '../../components/HelmetStyle';
import HelmetScript from '../../components/HelmetScript';
import HelmetHead from '../../components/HelmetHead';
import Loader from '../../components/Loader';
import UnderConstruction from '../../components/UnderConstruction';
import PageIntro from '../../components/PageIntro';
import GridList from '../../widgets/GridList';
import NoMatch from '../NoMatch';
import MemberOnly from '../../components/MemberOnly';

import config from '../../config';

const IndexPage = (props) => {
  const {
    match = {},
    i18n,
    indexPage,
    indexPageList,
    allowCookies,
    enableTealium,
    enableAdGlare,
    siteConfig,
  } = props;
  const notFound = useNotFound(indexPage);
  const [curPage, setCurPage] = useState(match.params.curPage || 1);
  const debouncePageLoading = useDebounceLoading(indexPage);
  const jsLoading = useDelayJsLoadiing(indexPage);
  const debounceListLoading = useDebounceLoading(indexPageList);

  const { data = {} } = indexPage || {};
  const content = ((data || {}).content || {}).content || {};
  const numberOfItemsPerPage = content.number_of_items_per_page || config.itemsPerPage;
  const lsQuery = content.indexPage_query || '';
  const sortBy = content.sortBy || '';

  const queryStart = () => {
    return (parseInt(curPage, 10) - 1) * numberOfItemsPerPage;
  };

  const prevPageName = usePrevious(match.params.pageName);
  useEffect(() => {
    if (prevPageName) {
      store.dispatch(resetIndexPages());
      store.dispatch(resetIndexPagesList());
    }
    store.dispatch(getIndexPages(match.params.pageName, match.params.curPage || 1));
  }, [match.params.pageName]);

  useAdGlare(i18n.locale, siteConfig, indexPage, enableAdGlare.status);

  useEffect(() => {
    if (lsQuery !== '') {
      setCurPage(match.params.curPage || 1);
    }
  }, [match.params.curPage]);

  useEffect(() => {
    const start = queryStart();
    if (lsQuery !== '') {
      // TODO: add sortBy
      store.dispatch(getIndexPagesList(lsQuery, start, numberOfItemsPerPage, sortBy));
    }
  }, [curPage]);

  useUnmount(() => {
    store.dispatch(resetIndexPages());
    store.dispatch(resetIndexPagesList());
  });

  const { locale } = i18n;
  const dataContent = content[locale] || {};
  const gridLayout = content.grid_layout || false;
  const { under_construction = false } = content;

  moment.locale(config.momentLangMap[locale]);

  // content
  const { headline = '', description = '', bottom_content = '' } = dataContent;
  const { lists = [], total = 0 } = indexPageList || {};

  // HelmetMeta
  const { metadata = {} } = data || {};
  const publishDate = metadata['TeamSite/LiveSite/PublishDate'];
  const seo = dataContent.seo || {};
  const { css_style_block = '', js_script_block = '', extra_head = '' } = content;

  const title = seo.seo_title;
  const [domTitle, setDomTitle] = useState('');
  const [isScriptReady, setIsScriptReady] = useState(false);

  useTealium(
    i18n.locale,
    siteConfig,
    indexPage,
    allowCookies.status,
    enableTealium.status,
    domTitle,
  );

  useURLChangeCallback(locale, data, null, [], isScriptReady);

  const helmetTitleCallBack = (newState) => {
    setDomTitle(newState.title);
  };

  const helmetScriptCallBack = (isReady) => {
    setIsScriptReady(isReady);
  };

  const getPageTypeCode = (pageType) => {
    // console.log(pageType);
    switch (pageType) {
      case config.pageType.pressReleasePage:
        return 'press-release';
      case config.pageType.promotionPage:
        return 'promotion';
      default:
        return 's';
    }
  };

  const gridEle = () => {
    const data = lists.map((v) => {
      const { fileName = '', content = {}, metadata = {} } = v;
      const displayPublishDate = content.contentPublishDate
        ? moment(momentUTCDate(content.contentPublishDate)).format('LL')
        : '';
      const pageContent = content[i18n.locale] || {};
      const pageType = metadata['TeamSite/Templating/DCR/Type'];
      const pageTypeCode = getPageTypeCode(pageType);
      const url = `${config.routePathTo}/${i18n.locale}/${pageTypeCode}/${fileName}`;
      const thumbnail = pageContent.thumbnail || config.indexPageDefaultThumbnail;
      return {
        displayPublishDate,
        ...pageContent,
        thumbnail,
        url,
      };
    });

    return <GridList data={data} />;
  };

  const listEle = (
    <ListGroup>
      {lists.map((p, i) => {
        const { fileName = '', content = {}, metadata = {} } = p;
        const pageContent = content[i18n.locale] || {};
        const pageType = metadata['TeamSite/Templating/DCR/Type'];
        const pageTypeCode = getPageTypeCode(pageType);
        const url = `${config.routePathTo}/${i18n.locale}/${pageTypeCode}/${fileName}`;
        return (
          <ListGroupItem key={i}>
            <Link to={url}>{pageContent.headline}</Link>
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );

  const paginationEle = () => {
    if (debounceListLoading) {
      return <Loader />;
    }
    return (
      <>
        {Math.ceil(total / numberOfItemsPerPage) > 1 && (
          <PaginationWidget
            itemsPerPage={numberOfItemsPerPage}
            total={total}
            activePage={curPage}
            linkPath={constructPath(config.routePathTo, i18n.locale, 'i', match.params.pageName)}
          />
        )}
      </>
    );
  };

  const headlineEle = <>{headline && <PageIntro headline={headline} />}</>;

  const headlineDesc = (
    <>{description && <div dangerouslySetInnerHTML={{ __html: sanitizedHTML(description) }} />}</>
  );

  const mainContent = (
    <>
      {gridLayout ? gridEle() : listEle}
      {paginationEle()}
    </>
  );

  const bottomContent = (
    <>
      {bottom_content && (
        <div dangerouslySetInnerHTML={{ __html: sanitizedHTML(bottom_content) }} />
      )}
    </>
  );

  // make sure SSO is ready
  const memberOnlyInd = metadata.memberOnly === 'true';

  const contentEle =
    memberOnlyInd && !isSSOLoggedIn() ? (
      <MemberOnly />
    ) : (
      <>
        <Container>
          {under_construction && <UnderConstruction />}
          {!under_construction && headlineDesc}
          {!under_construction && mainContent}
          {!under_construction && bottomContent}
        </Container>
        {!under_construction && (
          <HelmetScript
            helmetScriptCallBack={helmetScriptCallBack}
            extraHead={extra_head}
            jsScript={js_script_block}
          />
        )}
      </>
    );

  const pageEle = (
    <div>
      {notFound ? (
        <NoMatch />
      ) : (
        <div className="indexPage">
          {headlineEle}
          {contentEle}
        </div>
      )}
    </div>
  );

  return (
    <>
      {debouncePageLoading || jsLoading ? <Loader /> : pageEle}
      {!debouncePageLoading && <HelmetHead extraHead={extra_head} />}
      {!debouncePageLoading && <HelmetStyle stylesheet={css_style_block} />}
      {!debouncePageLoading && <HelmetTitle title={title} cb={helmetTitleCallBack} />}
      {!debouncePageLoading && <HelmetMeta seo={seo} publishDate={publishDate} />}
    </>
  );
};

const mapStateToProps = (state) => ({
  i18n: state.i18n,
  indexPage: state.indexPage,
  indexPageList: state.indexPageList,
  allowCookies: state.allowCookies,
  enableTealium: state.enableTealium,
  enableAdGlare: state.enableAdGlare,
  siteConfig: state.siteConfig,
});

const connectStore = connect(mapStateToProps, null, null, {
  pure: true,
})(IndexPage);

export { IndexPage as IndexPageComponent }; // <- For component Unit Test
export default connectStore;
